import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardScope } from '../contexts/DashboardScopeContext';
import apiClient from '../services/apiClient';
import { formatCurrency as standardFormatCurrency, formatPercentage as standardFormatPercentage } from '../utils/dashboardUtils';
import {
    collectSettledQuality,
    extractEndpointQuality,
} from '../utils/kpiQualityContract';

const RESULT_CACHE_TTL_MS = 3 * 60 * 1000;
const REFRESH_DEBOUNCE_MS = 300;
const FINANCIAL_ENDPOINT_CACHE_TTL_MS = 5 * 60 * 1000;
const financialDashboardResultCache = new Map();
const financialEndpointResultCache = new Map();
const financialEndpointInflight = new Map();
const isCypressTestRun = typeof window !== 'undefined' && Boolean(window.Cypress);

const normalizeRequestDate = (value) => {
    if (!value) {
        return null;
    }
    const toDate = value instanceof Date ? value : new Date(value);
    return Number.isNaN(toDate.getTime()) ? null : toDate.toISOString().split('T')[0];
};

const normalizeFilterValue = (value) => {
    if (value === null || value === undefined || value === '' || value === 'all') {
        return null;
    }
    return value;
};

const buildFinancialDashboardRefreshKey = (
    f,
    dateParams = {},
    userIdentifier = null,
    scopeProjectId = null,
    scopeProjectStatus = null
) => JSON.stringify({
    user: normalizeFilterValue(userIdentifier),
    scopeProjectId: normalizeFilterValue(scopeProjectId),
    scopeProjectStatus: normalizeFilterValue(scopeProjectStatus),
    contractType: normalizeFilterValue(f.contractType),
    profitability: normalizeFilterValue(f.profitability),
    clientId: normalizeFilterValue(f.clientId),
    start_date: dateParams.start_date || null,
    end_date: dateParams.end_date || null
});

export { buildFinancialDashboardRefreshKey };

const buildEndpointCacheKey = (url, { params = null, data = null } = {}) => {
    const normalizedParams = params
        ? Object.entries(params).sort(([left], [right]) => left.localeCompare(right))
            .reduce((acc, [key, value]) => {
                if (value !== undefined && value !== null) {
                    acc[key] = value;
                }
                return acc;
            }, {})
        : {};
    const normalizedData = data || null;
    return `${url}:${JSON.stringify({ params: normalizedParams, data: normalizedData })}`;
};

const getCachedEndpointResponse = (cacheKey, requestFactory) => {
    if (isCypressTestRun) {
        return requestFactory();
    }
    const cachedEntry = financialEndpointResultCache.get(cacheKey);
    if (cachedEntry && (Date.now() - cachedEntry.timestamp) < FINANCIAL_ENDPOINT_CACHE_TTL_MS) {
        return Promise.resolve(cachedEntry.response);
    }

    const inFlight = financialEndpointInflight.get(cacheKey);
    if (inFlight) {
        return inFlight;
    }

    const requestPromise = requestFactory().then((response) => {
        if (response) {
            financialEndpointResultCache.set(cacheKey, {
                timestamp: Date.now(),
                response
            });
        }
        return response;
    }).finally(() => {
        if (financialEndpointInflight.get(cacheKey) === requestPromise) {
            financialEndpointInflight.delete(cacheKey);
        }
    });

    financialEndpointInflight.set(cacheKey, requestPromise);
    return requestPromise;
};

const isCanceledLike = (reason) => {
    if (!reason || typeof reason !== 'object') return false;
    const code = reason.code;
    const name = reason.name;
    const msg = typeof reason.message === 'string' ? reason.message.toLowerCase() : '';
    return (
        code === 'ERR_CANCELED' ||
        name === 'CanceledError' ||
        msg === 'canceled'
    );
};

/** Normalize portfolio margin fields to percent points for percentage KPI cards. */
export const toPortfolioMarginPercentPoints = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return null;
    }
    const numeric = Number(value);
    return Math.abs(numeric) <= 1.5 ? numeric * 100 : numeric;
};

export const mapPortfolioDataFromPayload = (payload = null) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const rawMargin = payload.portfolio_profitability ?? payload.aggregate_profit_margin ?? null;

    return {
        // Canonical payloads may use either `total_revenue` or `total_portfolio_value`.
        total_revenue: payload.total_revenue ?? payload.total_portfolio_value ?? null,
        net_profit: payload.net_profit ?? null,
        total_costs: payload.total_costs ?? null,
        profit_margin: toPortfolioMarginPercentPoints(rawMargin),
        roi: payload.roi ?? null,
        portfolio_health_score: payload.portfolio_health_score ?? null,
        total_projects: payload.total_projects_analyzed ?? null,
        risk_level: payload.portfolio_risk_assessment?.risk_level || 'unknown',
        risk_factors: payload.portfolio_risk_assessment?.risk_factors || []
    };
};

export const normalizeUtilizationValue = (util) => {
    if (typeof util !== 'number' || !Number.isFinite(util)) {
        return null;
    }
    return util;
};

/**
 * Map utilization API payload to dashboard shape without cross-field fallbacks.
 *
 * @param {object|null|undefined} apiData - Utilization endpoint `data` object
 * @returns {object|null} Normalized utilization payload or null when input is empty
 */
export const mapUtilizationApiPayload = (apiData) => {
    if (!apiData || typeof apiData !== 'object') {
        return null;
    }

    return {
        users_analyzed: apiData.users_analyzed ?? 0,
        current: {
            average_utilization: normalizeUtilizationValue(apiData.current?.average_utilization),
            target_utilization: normalizeUtilizationValue(apiData.current?.target_utilization),
            billable_hours: apiData.current?.billable_hours ?? null,
            total_hours: apiData.current?.total_hours ?? null,
            utilization_variance: normalizeUtilizationValue(apiData.current?.utilization_variance),
            scheduled_utilization: normalizeUtilizationValue(
                apiData.current?.scheduled_utilization ?? apiData.current?.average_utilization
            ),
            actual_utilization: normalizeUtilizationValue(
                apiData.current?.actual_utilization ?? apiData.current?.average_utilization
            ),
        },
        billable_analysis: {
            billable_hours: apiData.billable_analysis?.billable_hours ?? null,
            non_billable_hours: apiData.billable_analysis?.non_billable_hours ?? null,
            total_capacity: apiData.billable_analysis?.total_capacity ?? null,
            billable_percentage: normalizeUtilizationValue(apiData.billable_analysis?.billable_percentage),
            efficiency_score: normalizeUtilizationValue(apiData.billable_analysis?.efficiency_score),
            scheduled_capacity: apiData.billable_analysis?.scheduled_capacity
                ?? apiData.billable_analysis?.total_capacity
                ?? null,
            actual_capacity: apiData.billable_analysis?.actual_capacity
                ?? apiData.billable_analysis?.total_capacity
                ?? null,
            excluded_hours: apiData.billable_analysis?.excluded_hours ?? null,
        },
        trend_data: (apiData.trend_data || []).map((trend) => ({
            ...trend,
            utilization: normalizeUtilizationValue(trend.utilization),
            actual: normalizeUtilizationValue(trend.actual),
            target: normalizeUtilizationValue(trend.target),
            forecast: normalizeUtilizationValue(trend.forecast),
        })),
        individual_resources: (apiData.individual_utilizations || []).map((resource) => ({
            ...resource,
            utilization: normalizeUtilizationValue(resource.utilization),
        })),
        workload_balance: apiData.workload_balance || {},
        recommendations: apiData.recommendations || [],
    };
};

/**
 * Enhanced Financial Dashboard Hook
 * Combines original FinancialDashboard capabilities with merged CostDashboard analytics
 * Follows established patterns from useFinancialEngine and useCostDashboard
 */
export const useEnhancedFinancialDashboard = () => {
    const { user, token } = useAuth();
    const {
        dateRange,
        projectId: scopeProjectId,
        projectStatus: scopeProjectStatus,
    } = useDashboardScope();
    const userId = user?.id;
    const updateFinanceHookDiagnostics = (payload = {}) => {
        if (typeof window === 'undefined') {
            return;
        }
        const currentDiagnostics = window.__financeHookDiagnostics || {};
        window.__financeHookDiagnostics = {
            ...currentDiagnostics,
            ...payload
        };
    };
    updateFinanceHookDiagnostics({
        initialized: true,
        userPresent: Boolean(user),
        tokenPresent: Boolean(token)
    });

    // State management
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const createEndpointLoadingState = (inFlight = false) => ({
        realization: inFlight,
        wip: inFlight,
        backlog: inFlight,
        billRates: inFlight,
        contributionMargin: inFlight,
        utilization: inFlight,
        arAging: inFlight,
        cashFlow: inFlight,
        expenseRatios: inFlight,
        benchmarkCompare: inFlight,
        revenueTriad: inFlight,
        portfolioHealth: inFlight,
        forecast: inFlight,
        enhancedForecast: inFlight
    });
    const [endpointLoadingState, setEndpointLoadingState] = useState(createEndpointLoadingState(false));

    // Original FinancialDashboard data
    const [profitabilityData, setProfitabilityData] = useState(null);
    const [revenueForecasting, setRevenueForecasting] = useState(null);
    const [financialMetrics, setFinancialMetrics] = useState(null);

    // Merged CostDashboard analytics
    const [costAnalytics, setCostAnalytics] = useState({
        budgetVariance: { projects: [] },
        profitabilityBreakdown: { by_project: [], by_department: [] },
        exportCapabilities: { formats: ['PDF', 'Excel', 'CSV'], status: 'ready' }
    });

    // Enhanced filtering capabilities (non-date overlays; dates come from DashboardScopeContext)
    const [filters, setFilters] = useState({
        contractType: 'all',
        profitability: 'all',
        clientId: 'all',
        serviceLine: null,
    });
    const [qualitySummary, setQualitySummary] = useState([]);

    // Use ref to store current filters to prevent unnecessary callback recreations
    const filtersRef = useRef(filters);
    /** Coalesce duplicate refreshes (e.g. React Strict Mode mount/unmount/remount). */
    const refreshInflightRef = useRef(null);
    /** Abort obsolete refreshes when a newer timeline/filter key starts. */
    const refreshAbortControllerRef = useRef(null);
    /** Only the latest run should clear loading. */
    const refreshRunIdRef = useRef(0);
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    // Dashboard raw data for calculations
    const [dashboardData, setDashboardData] = useState(null);
    // New KPI data (industry-standard)
    const [realizationData, setRealizationData] = useState(null);
    const [wipData, setWipData] = useState(null);
    const [backlogData, setBacklogData] = useState(null);
    const [billRateData, setBillRateData] = useState(null);
    const [contributionMarginData, setContributionMarginData] = useState(null);
    const [utilizationData, setUtilizationData] = useState(null);

    // Add new state
    const [arAgingData, setArAgingData] = useState(null);
    const [cashFlowData, setCashFlowData] = useState(null);
    const [expenseRatiosData, setExpenseRatiosData] = useState(null);
    const [benchmarkCompareData, setBenchmarkCompareData] = useState(null);
    const [revenueTriadData, setRevenueTriadData] = useState(null);

    // Period comparison state for PSA industry-standard reporting
    const [periodComparisonData, setPeriodComparisonData] = useState(null);
    const [showComparison, setShowComparison] = useState(false);

    const applyFinancialDashboardSnapshot = useCallback((snapshot) => {
        if (!snapshot) {
            return;
        }
        setProfitabilityData(snapshot.profitabilityData ?? null);
        setRevenueForecasting(snapshot.revenueForecasting ?? null);
        setFinancialMetrics(snapshot.financialMetrics ?? null);
        setDashboardData(snapshot.dashboardData ?? null);
        setRealizationData(snapshot.realizationData ?? null);
        setWipData(snapshot.wipData ?? null);
        setBacklogData(snapshot.backlogData ?? null);
        setBillRateData(snapshot.billRateData ?? null);
        setContributionMarginData(snapshot.contributionMarginData ?? null);
        setUtilizationData(snapshot.utilizationData ?? null);
        setArAgingData(snapshot.arAgingData ?? null);
        setCashFlowData(snapshot.cashFlowData ?? null);
        setExpenseRatiosData(snapshot.expenseRatiosData ?? null);
        setBenchmarkCompareData(snapshot.benchmarkCompareData ?? null);
        setRevenueTriadData(snapshot.revenueTriadData ?? null);
        setCostAnalytics(snapshot.costAnalytics ?? {
            budgetVariance: { projects: [] },
            profitabilityBreakdown: { by_project: [], by_department: [] },
            exportCapabilities: { formats: ['PDF', 'Excel', 'CSV'], status: 'ready' }
        });
        setQualitySummary(snapshot.qualitySummary ?? []);
        setError(null);
        setLoading(false);
        setEndpointLoadingState(createEndpointLoadingState(false));
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Load all financial and cost data
     */
    const refreshFinancialData = useCallback(async () => {
        const currentFilters = filtersRef.current;

        if (!userId || !token) {
            setLoading(false);
            return;
        }

        const formatDate = (date) => normalizeRequestDate(date);
        const resolveScopeDateParams = () => {
            const scopedStart = dateRange?.start_date || null;
            const scopedEnd = dateRange?.end_date || null;
            if (!scopedStart || !scopedEnd) {
                const fallbackEnd = new Date();
                const fallbackStart = new Date(fallbackEnd);
                fallbackStart.setDate(fallbackEnd.getDate() - 90);
                return {
                    startDateStr: formatDate(fallbackStart),
                    endDateStr: formatDate(fallbackEnd),
                    timeframeDays: 90,
                };
            }

            const startMs = new Date(scopedStart).getTime();
            const endMs = new Date(scopedEnd).getTime();
            const timeframeDays = Number.isFinite(startMs) && Number.isFinite(endMs)
                ? Math.max(1, Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)))
                : 90;

            return {
                startDateStr: scopedStart,
                endDateStr: scopedEnd,
                timeframeDays,
            };
        };

        const { startDateStr, endDateStr, timeframeDays } = resolveScopeDateParams();
        const dateParams = { start_date: startDateStr, end_date: endDateStr };
        const startedKey = buildFinancialDashboardRefreshKey(
            currentFilters,
            dateParams,
            userId,
            scopeProjectId,
            scopeProjectStatus
        );

        const inflight = refreshInflightRef.current;
        if (inflight?.key === startedKey) {
            return inflight.promise;
        }

        if (refreshAbortControllerRef.current?.key !== startedKey) {
            refreshAbortControllerRef.current?.controller.abort();
        }

        // Snapshot cache survives tab unmount/remount and restores the exact data for this key.
        const cachedSnapshot = isCypressTestRun
            ? null
            : financialDashboardResultCache.get(startedKey);
        if (cachedSnapshot && (Date.now() - cachedSnapshot.timestamp) < RESULT_CACHE_TTL_MS) {
            refreshAbortControllerRef.current = null;
            applyFinancialDashboardSnapshot(cachedSnapshot.data);
            return;
        }

        const abortController = new AbortController();
        refreshAbortControllerRef.current = {
            key: startedKey,
            controller: abortController
        };

        refreshRunIdRef.current += 1;
        const runId = refreshRunIdRef.current;

        const refreshPromise = (async () => {
            const isStale = () => buildFinancialDashboardRefreshKey(
                filtersRef.current,
                dateParams,
                userId,
                scopeProjectId,
                scopeProjectStatus
            ) !== startedKey;

            setLoading(true);
            setError(null);
            setEndpointLoadingState(createEndpointLoadingState(true));

            const markEndpointDone = (endpointKey) => {
                if (isStale()) {
                    return;
                }
                setEndpointLoadingState((current) => {
                    if (!current[endpointKey]) {
                        return current;
                    }
                    return {
                        ...current,
                        [endpointKey]: false
                    };
                });
            };

            let releasedPrimaryLoading = false;

            try {
                // Build filter parameters for all endpoints
                const filterParams = {
                    ...dateParams,
                    contract_type: normalizeFilterValue(currentFilters.contractType),
                    status: normalizeFilterValue(scopeProjectStatus),
                    profitability: normalizeFilterValue(currentFilters.profitability),
                    client_id: normalizeFilterValue(currentFilters.clientId),
                    project_id: scopeProjectId && scopeProjectId !== 'all'
                        ? scopeProjectId
                        : null,
                };
                Object.keys(filterParams).forEach((key) => {
                    if (filterParams[key] === undefined || filterParams[key] === null) {
                        delete filterParams[key];
                    }
                });

                const apiConfig = {
                    timeout: 180000, // 180 second timeout for complex financial calculations
                    signal: abortController.signal,
                };
                const utilizationMonths = timeframeDays <= 30 ? 1 : timeframeDays <= 90 ? 3 : timeframeDays <= 180 ? 6 : 12;

                const executeCachedGet = (url, requestConfig = {}) => {
                    const cacheKey = buildEndpointCacheKey(`user:${userId}|${url}`, { params: requestConfig.params });
                    return getCachedEndpointResponse(cacheKey, () => apiClient.get(url, requestConfig));
                };

                const executeCachedPost = (url, requestData = {}, requestConfig = {}) => {
                    const cacheKey = buildEndpointCacheKey(`user:${userId}|${url}`, { params: requestConfig.params, data: requestData });
                    return getCachedEndpointResponse(cacheKey, () => apiClient.post(url, requestData, requestConfig));
                };

                // -----------------------------------------------------------------
                // Primary KPIs: async-parallel + NO waterfall on the slowest endpoint.
                // Promise.allSettled waits for max(latency); commit each row as soon as
                // its response arrives so fast endpoints paint before slow ones (e.g. WIP).
                // -----------------------------------------------------------------
                const releasePrimaryLoadingOnce = () => {
                    if (releasedPrimaryLoading || isStale()) return;
                    releasedPrimaryLoading = true;
                    setLoading(false);
                };

                const realizationReq = executeCachedGet('financial-engine/realization/', { ...apiConfig, params: dateParams });
                realizationReq.then((resp) => {
                    if (isStale()) return;
                    if (resp?.data?.success) {
                        setRealizationData(resp.data.data);
                        releasePrimaryLoadingOnce();
                    }
                }).catch(() => { })
                    .finally(() => markEndpointDone('realization'));

                const wipReq = executeCachedGet('financial-engine/wip/', { ...apiConfig, params: filterParams });
                wipReq.then((resp) => {
                    if (isStale()) return;
                    if (resp?.data?.success) {
                        const wipPayload = resp.data.data || {};
                        const aging = wipPayload.aging_buckets || {};
                        const normalizedAging = Object.fromEntries(
                            Object.entries(aging).map(([k, v]) => [
                                k,
                                v === null || v === undefined || Number.isNaN(Number(v))
                                    ? null
                                    : Number(v)
                            ])
                        );
                        setWipData({
                            ...wipPayload,
                            aging_buckets: normalizedAging,
                            total_wip: wipPayload.total_wip ?? wipPayload.wip_value ?? null,
                        });
                    }
                }).catch(() => { })
                    .finally(() => markEndpointDone('wip'));

                const backlogReq = executeCachedGet('financial-engine/backlog/', { ...apiConfig, params: filterParams });
                backlogReq.then((resp) => {
                    if (isStale()) return;
                    if (resp?.data?.success) {
                        setBacklogData(resp.data.data);
                        releasePrimaryLoadingOnce();
                    }
                }).catch(() => { })
                    .finally(() => markEndpointDone('backlog'));

                const billRatesReq = executeCachedGet('financial-engine/bill-rates/', { ...apiConfig, params: dateParams });
                billRatesReq.then((resp) => {
                    if (isStale()) return;
                    if (resp?.data?.success) {
                        setBillRateData(resp.data.data);
                        releasePrimaryLoadingOnce();
                    }
                }).catch(() => { })
                    .finally(() => markEndpointDone('billRates'));

                const revenueTriadReq = executeCachedGet('financial-engine/revenue-triad/', { ...apiConfig, params: dateParams });
                revenueTriadReq.then((resp) => {
                    if (isStale()) return;
                    const root = resp?.data || null;
                    const payload = root?.data ?? root ?? null;
                    const hasPayload = payload && typeof payload === 'object' && (
                        Object.prototype.hasOwnProperty.call(payload, 'revenue_recognized') ||
                        Object.prototype.hasOwnProperty.call(payload, 'revenue_invoiced') ||
                        Object.prototype.hasOwnProperty.call(payload, 'cash_collected')
                    );
                    if (root?.success === true || hasPayload) {
                        setRevenueTriadData(payload);
                        releasePrimaryLoadingOnce();
                    }
                }).catch(() => { })
                    .finally(() => markEndpointDone('revenueTriad'));

                const contributionMarginReq = executeCachedGet('financial-engine/contribution-margin/', { ...apiConfig }); // No date params - portfolio-level metric
                const arAgingReq = executeCachedGet('financial-engine/ar-aging/', { ...apiConfig, params: filterParams });
                const cashFlowReq = executeCachedGet('financial-engine/cash-flow/', { ...apiConfig, params: filterParams });
                const expenseRatiosReq = executeCachedGet('analytics-engine/expense-ratios/', { ...apiConfig, params: filterParams });
                const benchmarkCompareReq = executeCachedGet('analytics-engine/benchmark-compare/', { ...apiConfig, params: filterParams });
                const enhancedForecastReq = executeCachedPost('financial-engine/enhanced-forecast/', filterParams, apiConfig);
                const utilizationReq = executeCachedGet('resource-optimization/utilization/', {
                    ...apiConfig,
                    params: {
                        months: utilizationMonths,
                        start_date: startDateStr,
                        end_date: endDateStr
                    }
                });

                const supportingKpiResultsPromise = Promise.allSettled([
                    contributionMarginReq,
                    arAgingReq,
                    cashFlowReq,
                    expenseRatiosReq,
                    benchmarkCompareReq,
                    enhancedForecastReq,
                    utilizationReq
                ]);

                contributionMarginReq.finally(() => markEndpointDone('contributionMargin'));
                arAgingReq.finally(() => markEndpointDone('arAging'));
                cashFlowReq.finally(() => markEndpointDone('cashFlow'));
                expenseRatiosReq.finally(() => markEndpointDone('expenseRatios'));
                benchmarkCompareReq.finally(() => markEndpointDone('benchmarkCompare'));
                enhancedForecastReq.finally(() => markEndpointDone('forecast'));
                utilizationReq.finally(() => markEndpointDone('utilization'));

                // Start portfolio + forecast in parallel with KPI batches, but do not await them before
                // primary KPIs — portfolio-health often dominates latency (60s+) and was blocking realization/WIP
                // updates (async waterfall / async-defer-await).
                const portfolioHealthResponsePromise = executeCachedGet('financial-engine/portfolio-health/', {
                    ...apiConfig,
                    params: dateParams
                });
                const forecastResponsePromise = executeCachedPost('financial-engine/revenue-forecast/', {
                    timeframe: timeframeDays,
                    confidence_level: 0.85
                }, apiConfig);

                portfolioHealthResponsePromise.finally(() => markEndpointDone('portfolioHealth'));
                forecastResponsePromise.finally(() => markEndpointDone('forecast'));

                const primaryKpiResultsPromise = Promise.allSettled([
                    realizationReq,
                    wipReq,
                    backlogReq,
                    billRatesReq,
                    revenueTriadReq
                ]);
                const criticalPrimaryResultsPromise = Promise.allSettled([
                    realizationReq,
                    backlogReq,
                    billRatesReq,
                    revenueTriadReq
                ]);
                const criticalPrimaryResults = await criticalPrimaryResultsPromise;
                if (!isStale()) {
                    releasePrimaryLoadingOnce();
                }
                const [realizationResp, wipResp, backlogResp, billRateResp, revenueTriadResp] = await primaryKpiResultsPromise;
                if (isStale()) {
                    return;
                }

                if (!releasedPrimaryLoading && criticalPrimaryResults.every((result) => result.status === 'rejected')) {
                    // Keep behavior consistent if all critical endpoints fail (and no callback has released).
                    releasePrimaryLoadingOnce();
                }

                const [portfolioHealthResponseResult, forecastResponseResult] = await Promise.allSettled([
                    portfolioHealthResponsePromise,
                    forecastResponsePromise
                ]);
                const portfolioHealthResponse = portfolioHealthResponseResult.status === 'fulfilled'
                    ? portfolioHealthResponseResult.value
                    : null;
                if (portfolioHealthResponseResult.status === 'rejected') {
                    console.error('Portfolio health endpoint failed:', portfolioHealthResponseResult.reason);
                }
                const forecastResponse = forecastResponseResult.status === 'fulfilled'
                    ? forecastResponseResult.value
                    : null;
                if (forecastResponseResult.status === 'rejected') {
                    console.error('Forecast endpoint failed:', forecastResponseResult.reason);
                }
                if (isStale()) {
                    return;
                }

                const portfolioSuccess = portfolioHealthResponse?.data?.success !== false;
                const portfolioData = portfolioSuccess ? (portfolioHealthResponse?.data?.data || portfolioHealthResponse?.data) : null;
                if (!portfolioSuccess && portfolioHealthResponse?.data?.error) {
                    console.error('Portfolio health error:', portfolioHealthResponse.data.error);
                }

                const forecastSuccess = forecastResponse?.data?.success !== false;
                const forecastData = forecastSuccess ? (forecastResponse?.data?.data || forecastResponse?.data) : null;
                if (!forecastSuccess && forecastResponse?.data?.error) {
                    console.error('Forecast error:', forecastResponse.data.error);
                }

                const mappedPortfolioData = mapPortfolioDataFromPayload(portfolioData);
                setProfitabilityData(mappedPortfolioData);

                const [contributionMarginResp, arResp, cashFlowResp, expenseRatiosResp, benchmarkResp, enhancedForecastResp, utilizationResp] = await supportingKpiResultsPromise;
                if (isStale()) {
                    return;
                }

                // Reuse portfolio health response for project cost analytics
                const costAnalyticsResp = portfolioHealthResponse || { data: null };

                // Skip fabricating per-project financial data; rely on real endpoints only
                const projectFinancialData = [];

                // Prefer enhanced forecast when available
                let enhancedForecast = null;
                if (enhancedForecastResp.status === 'fulfilled' && enhancedForecastResp.value.data?.success) {
                    enhancedForecast = enhancedForecastResp.value.data.data;
                }

                // Map revenue forecast data to frontend expected format
                const mappedForecastData = enhancedForecast ? {
                    components: {
                        actual_to_date: enhancedForecast.components?.actual_to_date ?? null,
                        scheduled_billings: enhancedForecast.components?.scheduled_billings ?? null,
                        wip_velocity: enhancedForecast.components?.wip_velocity ?? null,
                        total_forecast: enhancedForecast.components?.total_forecast ?? null
                    },
                    forecast: [
                        {
                            month: 'Current',
                            predicted: enhancedForecast.components?.total_forecast ?? null,
                            upper_bound: enhancedForecast.components?.upper_bound ?? null,
                            lower_bound: enhancedForecast.components?.lower_bound ?? null
                        }
                    ],
                    confidence: enhancedForecast.confidence?.score ?? null,
                    confidence_level: enhancedForecast.confidence?.level || 'unknown',
                    methodology: enhancedForecast.methodology || 'Scheduled + WIP velocity',
                    assumptions: enhancedForecast.assumptions || {},
                    growth_rate: null,
                    trend_direction: enhancedForecast.trend_direction || 'stable',
                    risk_factors: []
                } : forecastData ? {
                    forecast: [
                        {
                            month: 'Current',
                            predicted: forecastData.projected_revenue ?? null,
                            upper_bound: forecastData.confidence_intervals?.upper_bound ?? null,
                            lower_bound: forecastData.confidence_intervals?.lower_bound ?? null
                        }
                    ],
                    confidence: forecastData.confidence_intervals?.confidence_level ?? null,
                    methodology: 'Simple statistical trend (no ML)',
                    growth_rate: forecastData.trend_analysis?.growth_rate ?? null,
                    trend_direction: forecastData.trend_analysis?.trend_direction || 'stable',
                    risk_factors: forecastData.risk_factors || []
                } : null;

                // Ensure we have valid data structures
                setRevenueForecasting(mappedForecastData);

                setFinancialMetrics(projectFinancialData);
                setDashboardData(null);

                const qualitySummary = [
                    portfolioHealthResponse ? extractEndpointQuality(portfolioHealthResponse, 'financial-engine/portfolio-health/', 'financial/portfolio-health', {}) : null,
                    forecastResponse ? extractEndpointQuality(forecastResponse, 'financial-engine/revenue-forecast/', 'financial/revenue-forecast', { timeframe: timeframeDays }) : null,
                    collectSettledQuality(realizationResp, 'financial-engine/realization/', 'financial/realization', dateParams),
                    collectSettledQuality(wipResp, 'financial-engine/wip/', 'financial/wip', filterParams),
                    collectSettledQuality(backlogResp, 'financial-engine/backlog/', 'financial/backlog', filterParams),
                    collectSettledQuality(billRateResp, 'financial-engine/bill-rates/', 'financial/bill-rates', dateParams),
                    collectSettledQuality(contributionMarginResp, 'financial-engine/contribution-margin/', 'financial/contribution-margin', {}),
                    collectSettledQuality(arResp, 'financial-engine/ar-aging/', 'financial/ar-aging', filterParams),
                    collectSettledQuality(cashFlowResp, 'financial-engine/cash-flow/', 'financial/cash-flow', filterParams),
                    collectSettledQuality(expenseRatiosResp, 'analytics-engine/expense-ratios/', 'analytics/expense-ratios', filterParams),
                    collectSettledQuality(benchmarkResp, 'analytics-engine/benchmark-compare/', 'analytics/benchmark-compare', filterParams),
                    collectSettledQuality(enhancedForecastResp, 'financial-engine/enhanced-forecast/', 'financial/enhanced-forecast', filterParams),
                    collectSettledQuality(utilizationResp, 'resource-optimization/utilization/', 'utilization/resource', { months: utilizationMonths, ...dateParams }),
                    collectSettledQuality(revenueTriadResp, 'financial-engine/revenue-triad/', 'financial/revenue-triad', dateParams)
                ].filter(Boolean);
                setQualitySummary(qualitySummary);

                let mappedRealizationData = null;
                let mappedWipData = null;
                let mappedBacklogData = null;
                let mappedBillRateData = null;
                let mappedContributionMarginData = null;
                let mappedUtilizationData = null;
                let mappedArAgingData = null;
                let mappedCashFlowData = null;
                let mappedExpenseRatiosData = null;
                let mappedBenchmarkCompareData = null;
                let mappedRevenueTriadData = null;

                // Map DYNAMIC KPI data from responses (these SHOULD change with filters)
                if (realizationResp.status === 'fulfilled' && realizationResp.value.data?.success) {
                    mappedRealizationData = realizationResp.value.data.data;
                    setRealizationData(mappedRealizationData); // DYNAMIC - changes with date/project filters
                } else {
                    setRealizationData(null);
                    if (realizationResp.status === 'rejected') {
                        console.error('Realization endpoint failed:', realizationResp.reason);
                    } else if (realizationResp.value?.data?.success === false) {
                        console.error('Realization endpoint returned success: false:', realizationResp.value.data.error);
                    }
                }
                if (wipResp.status === 'fulfilled' && wipResp.value.data?.success) {
                    const wipPayload = wipResp.value.data.data || {};
                    // Normalize aging buckets to ensure numbers
                    const aging = wipPayload.aging_buckets || {};
                    const normalizedAging = Object.fromEntries(
                        Object.entries(aging).map(([k, v]) => [
                            k,
                            v === null || v === undefined || Number.isNaN(Number(v))
                                ? null
                                : Number(v)
                        ])
                    );
                    mappedWipData = {
                        ...wipPayload,
                        aging_buckets: normalizedAging,
                        total_wip: wipPayload.total_wip ?? wipPayload.wip_value ?? null,
                    };
                    setWipData(mappedWipData); // DYNAMIC - changes with date/project filters
                } else {
                    setWipData(null);
                    if (wipResp.status === 'rejected') {
                        console.error('WIP endpoint failed:', wipResp.reason);
                    } else if (wipResp.value?.data?.success === false) {
                        console.error('WIP endpoint returned success: false:', wipResp.value.data.error);
                    }
                }
                if (backlogResp.status === 'fulfilled' && backlogResp.value.data?.success) {
                    mappedBacklogData = backlogResp.value.data.data;
                    setBacklogData(mappedBacklogData); // DYNAMIC - changes with date/project filters
                } else {
                    setBacklogData(null);
                    if (backlogResp.status === 'rejected') {
                        console.error('Backlog endpoint failed:', backlogResp.reason);
                    } else if (backlogResp.value?.data?.success === false) {
                        console.error('Backlog endpoint returned success: false:', backlogResp.value.data.error);
                    }
                }

                if (billRateResp.status === 'fulfilled' && billRateResp.value.data?.success) {
                    mappedBillRateData = billRateResp.value.data.data;
                    setBillRateData(mappedBillRateData);
                } else {
                    setBillRateData(null);
                    if (billRateResp.status === 'rejected') {
                        console.error('Bill Rate endpoint failed:', billRateResp.reason);
                    } else if (billRateResp.value?.data?.success === false) {
                        console.error('Bill Rate endpoint returned success: false:', billRateResp.value.data.error);
                    }
                }
                if (contributionMarginResp.status === 'fulfilled' && contributionMarginResp.value.data?.success) {
                    mappedContributionMarginData = contributionMarginResp.value.data.data;
                    setContributionMarginData(mappedContributionMarginData); // DYNAMIC - changes with date/project filters
                } else {
                    setContributionMarginData(null);
                    if (contributionMarginResp.status === 'rejected') {
                        console.error('Contribution Margin endpoint failed:', contributionMarginResp.reason);
                    } else if (contributionMarginResp.value?.data?.success === false) {
                        console.error('Contribution Margin endpoint returned success: false:', contributionMarginResp.value.data.error);
                    }
                }

                // Process utilization data with proper normalization (no cross-field fallback)
                if (utilizationResp.status === 'fulfilled' && utilizationResp.value.data?.success) {
                    mappedUtilizationData = mapUtilizationApiPayload(utilizationResp.value.data.data);
                    setUtilizationData(mappedUtilizationData);
                } else {
                    setUtilizationData(null);

                    if (utilizationResp.status === 'rejected') {
                        console.error('Utilization endpoint rejected:', utilizationResp.reason);
                    } else if (utilizationResp.value?.data?.success === false) {
                        console.error('Utilization endpoint error:', utilizationResp.value.data.error);
                    }
                }

                // Map AR & Cash Flow
                if (arResp.status === 'fulfilled' && arResp.value.data?.success) {
                    mappedArAgingData = arResp.value.data.data;
                    setArAgingData(mappedArAgingData);
                } else {
                    setArAgingData(null);
                    if (arResp.status === 'rejected') {
                        console.error('AR Aging endpoint failed:', arResp.reason);
                    } else if (arResp.value?.data?.success === false) {
                        console.error('AR Aging endpoint returned success: false:', arResp.value.data.error);
                    }
                }
                if (cashFlowResp.status === 'fulfilled' && cashFlowResp.value.data?.success) {
                    mappedCashFlowData = cashFlowResp.value.data.data;
                    setCashFlowData(mappedCashFlowData);
                } else {
                    setCashFlowData(null);
                    if (cashFlowResp.status === 'rejected') {
                        console.error('Cash Flow endpoint failed:', cashFlowResp.reason);
                    } else if (cashFlowResp.value?.data?.success === false) {
                        console.error('Cash Flow endpoint returned success: false:', cashFlowResp.value.data.error);
                    }
                }

                // Map Expense Ratios & Benchmark Compare
                if (expenseRatiosResp.status === 'fulfilled' && expenseRatiosResp.value.data?.success) {
                    mappedExpenseRatiosData = expenseRatiosResp.value.data.data;
                    setExpenseRatiosData(mappedExpenseRatiosData);
                } else {
                    setExpenseRatiosData(null);
                    if (expenseRatiosResp.status === 'rejected') {
                        console.error('Expense Ratios endpoint failed:', expenseRatiosResp.reason);
                    } else if (expenseRatiosResp.value?.data?.success === false) {
                        console.error('Expense Ratios endpoint returned success: false:', expenseRatiosResp.value.data.error);
                    }
                }
                if (benchmarkResp.status === 'fulfilled' && benchmarkResp.value.data?.success) {
                    mappedBenchmarkCompareData = benchmarkResp.value.data.data;
                    setBenchmarkCompareData(mappedBenchmarkCompareData);
                } else {
                    setBenchmarkCompareData(null);
                    if (benchmarkResp.status === 'rejected') {
                        console.error('Benchmark Compare endpoint failed:', benchmarkResp.reason);
                    } else if (benchmarkResp.value?.data?.success === false) {
                        console.error('Benchmark Compare endpoint returned success: false:', benchmarkResp.value.data.error);
                    }
                }

                // Process revenue triad data (industry-standard PSA metrics)
                const revenueTriadResponse = revenueTriadResp.status === 'fulfilled' ? (revenueTriadResp.value?.data || null) : null;
                const revenueTriadPayload = revenueTriadResponse?.data ?? revenueTriadResponse ?? null;
                const hasRevenueTriadPayload = revenueTriadPayload && typeof revenueTriadPayload === 'object' && (
                    Object.prototype.hasOwnProperty.call(revenueTriadPayload, 'revenue_recognized') ||
                    Object.prototype.hasOwnProperty.call(revenueTriadPayload, 'revenue_invoiced') ||
                    Object.prototype.hasOwnProperty.call(revenueTriadPayload, 'cash_collected')
                );
                const revenueTriadAccepted = (
                    revenueTriadResp.status === 'fulfilled' &&
                    (
                        revenueTriadResponse?.success === true ||
                        hasRevenueTriadPayload
                    )
                );

                if (revenueTriadAccepted) {
                    mappedRevenueTriadData = revenueTriadPayload;
                    setRevenueTriadData(mappedRevenueTriadData);
                    updateFinanceHookDiagnostics({
                        revenueTriadAccepted: true,
                        revenueTriadPayloadSource: revenueTriadResponse?.data ? 'data-nested' : 'data-root',
                        revenueTriadRecognized: revenueTriadPayload?.revenue_recognized ?? null,
                        revenueTriadInvoiced: revenueTriadPayload?.revenue_invoiced ?? null,
                        revenueTriadCollected: revenueTriadPayload?.cash_collected ?? null
                    });
                } else {
                    setRevenueTriadData(null);
                    updateFinanceHookDiagnostics({
                        revenueTriadAccepted: false,
                        revenueTriadResponseStatus: revenueTriadResp.status
                    });
                    if (revenueTriadResp.status === 'rejected') {
                        console.error('Revenue Triad endpoint failed:', revenueTriadResp.reason);
                    } else {
                        console.error('Revenue Triad endpoint returned invalid payload:', revenueTriadResponse);
                    }
                }

                // Process cost analytics from real backend data
                const costAnalyticsSuccess = costAnalyticsResp?.data?.success !== false;
                const costAnalyticsData = costAnalyticsSuccess ? (costAnalyticsResp.data?.data || costAnalyticsResp.data) : null;

                const enhancedCostAnalytics = costAnalyticsData ? {
                    profitabilityBreakdown: {
                        by_project: (costAnalyticsData.project_performance_ranking || []).slice(0, 5).map(p => ({
                            name: p.name || `Project ${p.project_id}`,
                            profit: p.profit_margin !== null && p.profit_margin !== undefined && p.total_revenue !== null && p.total_revenue !== undefined ? p.total_revenue * p.profit_margin : null,
                            margin: p.profit_margin ?? null
                        })),
                        by_department: [] // Will be populated when department data is available
                    },
                    budgetVariance: {
                        projects: (costAnalyticsData.project_performance_ranking || []).map(p => ({
                            id: p.project_id,
                            name: p.project_name || `Project ${p.project_id}`, // Use project_name from backend
                            budget: p.budget, // Preserve null - don't fabricate 0
                            actual_cost: p.total_costs, // Preserve null - don't fabricate 0
                            variance: p.budget !== null && p.total_costs !== null ? p.total_costs - p.budget : null,
                            variance_percentage: p.budget && p.total_costs !== null ? ((p.total_costs - p.budget) / p.budget) * 100 : null,
                            status: p.status, // Preserve null - don't fabricate 'active'
                            contract_type: p.contract_type, // Preserve null - don't fabricate 'lump_sum'
                            total_revenue: p.total_revenue, // Preserve null - don't fabricate 0
                            profit_margin: p.profit_margin // Preserve null - don't fabricate 0
                        }))
                    },
                    exportCapabilities: { formats: ['PDF', 'Excel', 'CSV'], status: 'ready' }
                } : {
                    // Return empty data to indicate API failure - frontend will show error state
                    profitabilityBreakdown: { by_project: [], by_department: [] },
                    budgetVariance: { projects: [] },
                    exportCapabilities: { formats: ['PDF', 'Excel', 'CSV'], status: 'error' }
                };

                setCostAnalytics(enhancedCostAnalytics);
                financialDashboardResultCache.set(startedKey, {
                    timestamp: Date.now(),
                    data: {
                        profitabilityData: mappedPortfolioData,
                        revenueForecasting: mappedForecastData,
                        financialMetrics: projectFinancialData,
                        dashboardData: null,
                        realizationData: mappedRealizationData,
                        wipData: mappedWipData,
                        backlogData: mappedBacklogData,
                        billRateData: mappedBillRateData,
                        contributionMarginData: mappedContributionMarginData,
                        utilizationData: mappedUtilizationData,
                        arAgingData: mappedArAgingData,
                        cashFlowData: mappedCashFlowData,
                        expenseRatiosData: mappedExpenseRatiosData,
                        benchmarkCompareData: mappedBenchmarkCompareData,
                        revenueTriadData: mappedRevenueTriadData,
                        costAnalytics: enhancedCostAnalytics,
                        qualitySummary
                    }
                });

                if (typeof window !== 'undefined') {
                    window.__financeHookState = {
                        loading: false,
                        profitabilityData: mappedPortfolioData,
                        revenueForecasting: mappedForecastData,
                        error: null,
                        qualitySummary,
                        timestamp: Date.now(),
                        source: 'refreshFinancialData'
                    };
                }
                updateFinanceHookDiagnostics({
                    refreshCompleted: true,
                    refreshCompletedAt: Date.now(),
                    refreshQualitySummary: qualitySummary
                });

            } catch (err) {
                if (isCanceledLike(err)) {
                    return;
                }
                console.error('Enhanced Financial Dashboard Error:', err);
                if (releasedPrimaryLoading) {
                    console.warn(
                        'Financial dashboard refresh had a non-fatal error after partial KPI data loaded:',
                        err
                    );
                    clearError();
                    return;
                }
                const errorMessage = err.response?.data?.detail ||
                    err.response?.data?.error ||
                    err.message ||
                    'Failed to load financial data. Please check your connection and try again.';
                setError(errorMessage);
                setQualitySummary([]);

                // Set null data to indicate failure (don't use fallback data)
                setProfitabilityData(null);
                setRevenueForecasting(null);
                if (typeof window !== 'undefined') {
                    window.__financeHookState = {
                        loading: false,
                        profitabilityData: null,
                        error: errorMessage,
                        qualitySummary: [],
                        timestamp: Date.now(),
                        source: 'refreshFinancialData'
                    };
                }
                updateFinanceHookDiagnostics({
                    refreshCompleted: true,
                    refreshCompletedAt: Date.now(),
                    refreshQualitySummary: []
                });

                // Log specific endpoint failures for debugging
                console.error('Financial dashboard initialization failed:', {
                    error: err.message,
                    status: err.response?.status,
                    url: err.config?.url
                });
            } finally {
                if (runId === refreshRunIdRef.current) {
                    setLoading(false);
                    setEndpointLoadingState(createEndpointLoadingState(false));
                }
                if (refreshAbortControllerRef.current?.controller === abortController) {
                    refreshAbortControllerRef.current = null;
                }
            }
        })();

        refreshInflightRef.current = { key: startedKey, promise: refreshPromise };
        refreshPromise.finally(() => {
            if (refreshInflightRef.current?.promise === refreshPromise) {
                refreshInflightRef.current = null;
            }
        });

        return refreshPromise;
    }, [userId, token, clearError, applyFinancialDashboardSnapshot, dateRange, scopeProjectId]);

    /**
     * Generate revenue forecast with enhanced analytics
     */
    const generateRevenueForecast = useCallback(async (forecastParams = {}) => {
        if (!user || !token) return;

        setLoading(true);
        try {
            const currentFilters = filtersRef.current;
            const scopedDays = (() => {
                if (!dateRange?.start_date || !dateRange?.end_date) {
                    return null;
                }
                const startMs = new Date(dateRange.start_date).getTime();
                const endMs = new Date(dateRange.end_date).getTime();
                if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
                    return null;
                }
                return Math.max(1, Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)));
            })();
            const resolvedTimeframe = Number.isFinite(Number(forecastParams.timeframe))
                ? Number(forecastParams.timeframe)
                : scopedDays;
            const payload = {
                include_cost_analysis: true
            };
            if (resolvedTimeframe !== null) {
                payload.timeframe = resolvedTimeframe;
            }
            if (Number.isFinite(Number(forecastParams.confidence_level))) {
                payload.confidence_level = Number(forecastParams.confidence_level);
            }
            const response = await apiClient.post('financial-engine/revenue-forecast/', payload);

            setRevenueForecasting(response.data?.data || response.data);
        } catch (err) {
            console.error('Revenue Forecast Error:', err);
        } finally {
            setLoading(false);
        }
    }, [user, token, dateRange]);

    /**
     * Update filters and refresh related data
     */
    const updateFilters = useCallback(async (newFilters) => {
        const nextFilters = { ...filters, ...newFilters };
        setFilters(nextFilters);
    }, [filters]);

    /**
     * Export data functionality - real CSV export, PDF/Excel status
     */
    const exportData = useCallback(async (format = 'CSV') => {
        if (!user || !token) return { success: false, error: 'Not authenticated' };

        try {
            setLoading(true);

            const formatUpper = format.toUpperCase();

            if (formatUpper === 'CSV') {
                // Generate real CSV from current data
                const csvRows = [];
                const toExportValue = (value, scale = 1) => {
                    if (value === null || value === undefined || Number.isNaN(Number(value))) {
                        return 'N/A';
                    }
                    return `${Number(value) * scale}`;
                };

                // Header
                csvRows.push('Financial Dashboard Export');
                csvRows.push(`Generated: ${new Date().toISOString()}`);
                csvRows.push(`Period: ${dateRange?.start_date || 'unknown'} to ${dateRange?.end_date || 'unknown'}`);
                csvRows.push('');

                // Portfolio Summary
                csvRows.push('Portfolio Summary');
                csvRows.push('Metric,Value');
                if (profitabilityData) {
                    csvRows.push(`Total Revenue,${toExportValue(profitabilityData.total_revenue)}`);
                    csvRows.push(`Net Profit,${toExportValue(profitabilityData.net_profit)}`);
                    csvRows.push(`Profit Margin,${toExportValue(profitabilityData.profit_margin, 100)}%`);
                }
                if (realizationData) {
                    csvRows.push(`Realization Rate,${toExportValue(realizationData.realization_rate, 100)}%`);
                }
                if (wipData) {
                    csvRows.push(`WIP Total,${toExportValue(wipData.total_wip)}`);
                }
                if (backlogData) {
                    csvRows.push(`Backlog Total,${toExportValue(backlogData.total_backlog)}`);
                }
                if (arAgingData) {
                    csvRows.push(`AR Total,${toExportValue(arAgingData.total_ar)}`);
                    csvRows.push(`DSO,${toExportValue(arAgingData.dso)}`);
                }

                // Revenue Triad
                if (revenueTriadData) {
                    csvRows.push('');
                    csvRows.push('Revenue Triad (ASC 606)');
                    csvRows.push('Metric,Value');
                    csvRows.push(`Revenue Recognized,${toExportValue(revenueTriadData.revenue_recognized)}`);
                    csvRows.push(`Revenue Invoiced,${toExportValue(revenueTriadData.revenue_invoiced)}`);
                    csvRows.push(`Cash Collected,${toExportValue(revenueTriadData.cash_collected)}`);
                }

                // Project Performance Table
                if (costAnalytics?.budgetVariance?.projects?.length > 0) {
                    csvRows.push('');
                    csvRows.push('Project Performance');
                    csvRows.push('Project,Budget,Actual Cost,Revenue,Profit/Loss,Margin %,Status');
                    costAnalytics.budgetVariance.projects.forEach(p => {
                        const profitLoss = (p.total_revenue === null || p.total_revenue === undefined || p.actual_cost === null || p.actual_cost === undefined)
                            ? 'N/A'
                            : Number(p.total_revenue) - Number(p.actual_cost);
                        const totalRevenue = Number(p.total_revenue);
                        const numericProfitLoss = Number(profitLoss);
                        const margin = Number.isFinite(totalRevenue) && totalRevenue !== 0
                            && Number.isFinite(numericProfitLoss)
                            && profitLoss !== 'N/A'
                            ? (numericProfitLoss / totalRevenue * 100).toFixed(1)
                            : 'N/A';
                        csvRows.push(`"${p.name || 'Unknown'}",${toExportValue(p.budget)},${toExportValue(p.actual_cost)},${toExportValue(p.total_revenue)},${profitLoss},${margin}%,${p.status || 'Unknown'}`);
                    });
                }

                // Create and download file
                const csvContent = csvRows.join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `financial-report-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                return {
                    success: true,
                    format: 'CSV',
                    filename: link.download
                };
            } else if (formatUpper === 'PDF' || formatUpper === 'EXCEL') {
                // PDF and Excel require backend support
                // For now, return not-available status rather than mocking
                return {
                    success: false,
                    error: `${formatUpper} export requires backend report generation service. Use CSV export for now.`,
                    availableFormats: ['CSV']
                };
            } else {
                return {
                    success: false,
                    error: `Unsupported export format: ${format}`,
                    availableFormats: ['CSV']
                };
            }
        } catch (err) {
            console.error('Export Error:', err);
            setError(`Failed to export data as ${format}`);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [user, token, profitabilityData, costAnalytics, filters, realizationData, wipData, backlogData, arAgingData, revenueTriadData]);

    /**
     * Utility functions for data formatting - Using standardized formatting
     */
    const formatCurrency = useCallback((amount) => {
        if (amount == null || Number.isNaN(Number(amount))) return 'N/A';
        return standardFormatCurrency(amount);
    }, []);

    const formatPercentage = useCallback((value) => {
        if (value == null || Number.isNaN(Number(value))) return 'N/A';
        // If value is already a percentage (> 1), don't multiply by 100
        // If value is a fraction (<= 1), multiply by 100
        const percentage = value > 1 ? value : value * 100;
        return standardFormatPercentage(percentage);
    }, []);

    /**
     * Filtered and calculated data based on current filters
     */
    const filteredAnalytics = useMemo(() => {
        if (!costAnalytics.budgetVariance.projects) return costAnalytics;

        const filteredProjects = costAnalytics.budgetVariance.projects.filter(project => {
            if (
                scopeProjectId
                && scopeProjectId !== 'all'
                && `${project.id}` !== `${scopeProjectId}`
                && `${project.project_id}` !== `${scopeProjectId}`
            ) {
                return false;
            }

            if (
                scopeProjectStatus
                && scopeProjectStatus !== 'all'
                && project.status !== scopeProjectStatus
            ) {
                return false;
            }

            // Contract type filter
            if (filters.contractType !== 'all' && project.contract_type !== filters.contractType) {
                return false;
            }

            // Profitability filter
            if (filters.profitability !== 'all') {
                const profitLoss = typeof project.profit_loss === 'number' ? project.profit_loss : null;
                if (filters.profitability === 'profitable' && (profitLoss === null || profitLoss <= 0)) {
                    return false;
                }
                if (filters.profitability === 'unprofitable' && (profitLoss === null || profitLoss > 0)) {
                    return false;
                }
            }

            return true;
        });

        return {
            ...costAnalytics,
            budgetVariance: { projects: filteredProjects }
        };
    }, [costAnalytics, filters, scopeProjectId, scopeProjectStatus]);

    // Load data on mount and when global scope or overlay filters change (debounced).
    useEffect(() => {
        if (!token || !userId) return;

        const timeoutId = setTimeout(() => {
            refreshFinancialData().catch((error) => {
                if (!isCanceledLike(error)) {
                    console.error('Periodic financial refresh failed:', error);
                }
            });
        }, REFRESH_DEBOUNCE_MS);

        return () => {
            clearTimeout(timeoutId);
            // Abort any in-flight requests from the previous timeline so they don't
            // pile up on the server while new requests are starting.
            refreshAbortControllerRef.current?.controller.abort();
            refreshAbortControllerRef.current = null;
            refreshInflightRef.current = null;
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dateRange?.start_date,
        dateRange?.end_date,
        scopeProjectId,
        scopeProjectStatus,
        filters.contractType,
        filters.profitability,
        filters.clientId,
        token,
        userId,
    ]);

    /**
     * Fetch period comparison data for trend analysis
     */
    const fetchPeriodComparison = useCallback(async () => {
        if (!user || !token) return;

        if (!dateRange?.start_date || !dateRange?.end_date) {
            return;
        }

        try {
            const response = await apiClient.get('financial-engine/period-comparison/', {
                params: {
                    current_start: dateRange.start_date,
                    current_end: dateRange.end_date
                },
                timeout: 15000
            });

            if (response.data?.success) {
                setPeriodComparisonData(response.data.data);
            }
        } catch (err) {
            console.error('Period comparison fetch failed:', err);
            setPeriodComparisonData(null);
        }
    }, [user, token, dateRange]);

    /**
     * Toggle period comparison mode
     */
    const toggleComparison = useCallback(() => {
        setShowComparison(prev => {
            const newValue = !prev;
            if (newValue && !periodComparisonData) {
                fetchPeriodComparison();
            }
            return newValue;
        });
    }, [periodComparisonData, fetchPeriodComparison]);

    return {
        // Data
        profitabilityData,
        revenueForecasting,
        financialMetrics,
        costAnalytics: filteredAnalytics,
        dashboardData,
        realizationData,
        wipData,
        backlogData,
        billRateData,
        contributionMarginData,
        utilizationData,
        endpointLoadingState,
        loading,
        error,
        filters,
        arAgingData,
        cashFlowData,
        expenseRatiosData,
        benchmarkCompareData,
        revenueTriadData,  // Industry-standard PSA metrics: recognized, invoiced, cash collected
        qualitySummary,

        // Period comparison (PSA industry standard)
        periodComparisonData,
        showComparison,

        // Operations
        refreshFinancialData,
        generateRevenueForecast,
        updateFilters,
        exportData,
        clearError,
        toggleComparison,
        fetchPeriodComparison,

        // Utilities
        formatCurrency,
        formatPercentage
    };
}; 